/**
 * Platform Admin — Agent Runtime identities (separate from Workers).
 * Never displays secrets / Codex tokens / tenant content.
 */
import { Button, Input, Modal, Space, Table, Tag, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import {
  platformCreateAgentEnrollmentToken,
  platformDisableAgentRuntime,
  platformEnableAgentRuntime,
  platformListAgentEnrollmentTokens,
  platformListAgentRuntimes,
  platformRevokeAgentCredential,
  platformRevokeAgentEnrollmentToken,
  type AgentRuntimeRow,
} from '../api'
import { SectionCard } from './adminShared'

const STATUS_COLOR: Record<string, string> = {
  ready: 'success',
  online_not_ready: 'warning',
  offline: 'default',
  disabled: 'error',
  revoked: 'error',
}

export default function AgentRuntimesPage() {
  const [agents, setAgents] = useState<AgentRuntimeRow[]>([])
  const [tokens, setTokens] = useState<
    Array<{
      id: string
      label: string | null
      environment: string
      expires_at: string | null
      use_count: number
      max_uses: number
      revoked: boolean
      last_used_by_agent_id: string | null
    }>
  >([])
  const [loading, setLoading] = useState(true)
  const [label, setLabel] = useState('pc1-agent')
  const [rawToken, setRawToken] = useState<string | null>(null)

  const load = useCallback(() => {
    setLoading(true)
    Promise.all([platformListAgentRuntimes(), platformListAgentEnrollmentTokens()])
      .then(([a, t]) => {
        setAgents(a.data.items || [])
        setTokens(t.data.items || [])
      })
      .catch((err) => message.error((err as Error).message || '加载失败'))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const createToken = async () => {
    try {
      const res = await platformCreateAgentEnrollmentToken({
        label,
        ttl_seconds: 86400,
        max_uses: 1,
        environment: 'preview',
      })
      setRawToken(res.data.enrollment_token)
      message.success('Enrollment token 已创建（仅显示一次）')
      load()
    } catch (err) {
      message.error((err as Error).message || '创建失败')
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <SectionCard title="Agent Runtime">
        <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
          Agent 与 Worker 是不同身份。这里只管理 Agent Runtime 注册、在线/就绪与凭证吊销，不显示密钥或租户内容。
        </div>
        <Table
          rowKey="agent_id"
          loading={loading}
          dataSource={agents}
          pagination={false}
          size="small"
          columns={[
            { title: 'Agent ID', dataIndex: 'agent_id', width: 180 },
            { title: '名称', dataIndex: 'name', width: 160 },
            { title: '环境', dataIndex: 'environment', width: 90 },
            {
              title: '状态',
              dataIndex: 'status',
              width: 140,
              render: (v: string) => <Tag color={STATUS_COLOR[v] || 'default'}>{v}</Tag>,
            },
            { title: 'Runtime', dataIndex: 'runtime_version', width: 100 },
            { title: '最近心跳', dataIndex: 'last_seen_at', width: 200 },
            {
              title: '操作',
              key: 'actions',
              render: (_: unknown, row: AgentRuntimeRow) => (
                <Space size="small">
                  {row.control_status === 'enabled' ? (
                    <Button size="small" onClick={() => platformDisableAgentRuntime(row.agent_id).then(load)}>
                      禁用
                    </Button>
                  ) : (
                    <Button size="small" type="primary" onClick={() => platformEnableAgentRuntime(row.agent_id).then(load)}>
                      启用
                    </Button>
                  )}
                  <Button
                    size="small"
                    danger
                    disabled={row.credential_revoked}
                    onClick={() => platformRevokeAgentCredential(row.agent_id).then(load)}
                  >
                    吊销凭证
                  </Button>
                </Space>
              ),
            },
          ]}
        />
      </SectionCard>

      <SectionCard
        title="Enrollment Tokens"
        extra={
          <Space>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} style={{ width: 180 }} placeholder="label" />
            <Button type="primary" onClick={createToken}>
              创建 Token
            </Button>
            <Button onClick={load}>刷新</Button>
          </Space>
        }
      >
        <Table
          rowKey="id"
          loading={loading}
          dataSource={tokens}
          pagination={false}
          size="small"
          columns={[
            { title: 'Label', dataIndex: 'label' },
            { title: 'Environment', dataIndex: 'environment', width: 100 },
            { title: 'Uses', render: (_: unknown, r) => `${r.use_count}/${r.max_uses}`, width: 80 },
            { title: 'Expires', dataIndex: 'expires_at', width: 200 },
            { title: 'Last Agent', dataIndex: 'last_used_by_agent_id', width: 160 },
            {
              title: 'Revoked',
              dataIndex: 'revoked',
              width: 90,
              render: (v: boolean) => (v ? <Tag color="error">yes</Tag> : <Tag>no</Tag>),
            },
            {
              title: '操作',
              key: 'act',
              render: (_: unknown, row) =>
                row.revoked ? null : (
                  <Button size="small" danger onClick={() => platformRevokeAgentEnrollmentToken(row.id).then(load)}>
                    吊销
                  </Button>
                ),
            },
          ]}
        />
      </SectionCard>

      <Modal
        open={!!rawToken}
        title="Enrollment Token（仅显示一次）"
        onCancel={() => setRawToken(null)}
        onOk={() => setRawToken(null)}
        okText="已复制，关闭"
      >
        <p>请立即写入 PC1 本机 `agent-runtime.env`，关闭后无法再次查看。</p>
        <Input.TextArea value={rawToken || ''} readOnly rows={3} />
      </Modal>
    </div>
  )
}
